/**
 * Camada 1 · a `FonteCentral` servida pelos SERVICOS, e nao pela memoria.
 *
 * ===========================================================================
 * A DECISAO DE ARQUITETURA, medida em 05/09/2026: AS CONVERSAS SAO DO MESSAGING
 * ===========================================================================
 * `FonteCentral` tem SETE metodos. Quatro sao de CONHECIMENTO e tres sao de CONVERSA, e a
 * pergunta que este arquivo responde e onde cada metade mora.
 *
 * O QUE FOI MEDIDO, e nao lembrado:
 *
 *   · `public.conversas` e `public.mensagens` nascem na migration 0010, com as policies de
 *     isolamento por cliente. A 0023 acrescentou a `public.conversas` exatamente os campos
 *     que `ConversaAjuda` precisa: `tipo`, `categoria_id` e `estado`.
 *   · `services/messaging` ja tem as rotas que leem e escrevem essas duas tabelas, e o
 *     `packages/chat` ja as consome no painel de quem opera.
 *   · `services/knowledge` nao toca nenhuma das duas, e nao tem rota de conversa.
 *
 * ENTAO O QUE FALTAVA NAO ERA CRIAR ROTA DE CONVERSA NO KNOWLEDGE. Criar la produziria dois
 * servicos escrevendo nas MESMAS tabelas, com duas transicoes de estado, dois jeitos de
 * contar nao lidas e duas ordens de lista. O sino do `packages/chat` conta de
 * `public.mensagens`: uma segunda escrita passaria a fazer o sino e a Central discordarem
 * sobre a mesma conversa. Duas fontes para o mesmo dado e precisamente o defeito que este
 * trabalho existe para fechar, e resolve-lo criando outro seria trocar de lugar em vez de
 * fechar.
 *
 * O QUE FALTAVA ERA ESTA PECA: uma `FonteCentral` que sabe falar com os DOIS servicos, e que
 * decide por metodo com quem falar. Conhecimento vai ao `services/knowledge`; conversa vai ao
 * `services/messaging`. Cada tabela continua tendo um dono, e a tela continua tendo um
 * contrato so.
 *
 * ===========================================================================
 * POR QUE ELA MORA AQUI E NAO EM CADA APP
 * ===========================================================================
 * Mesma razao ja escrita em `fonte-http.ts` e em `packages/chat/src/transporte-http.ts`:
 * escrita no app, ela nasceria uma vez por produto, e as copias divergiriam na primeira
 * mudanca de rota. A que envelhece e a do produto que ninguem abriu naquela semana.
 *
 * ===========================================================================
 * O QUE ELA NAO RESOLVE, e esta escrito em vez de escondido
 * ===========================================================================
 * `NovaConversa.categoriaSugerida` (passo 21 do manual) NAO TEM COLUNA no banco: a 0023 criou
 * `conversas.categoria_id`, que aponta para uma categoria que EXISTE, e nao ha campo para o
 * nome de uma que falta. Ver `abrirConversa` para o que este arquivo faz com ele, e por que.
 */

import type {
  CategoriaAjuda,
  ConversaAjuda,
  FonteCentral,
  NovaConversa,
  SituacaoDaConversa,
  TipoDeConversa,
  TurnoDaConversa,
  ArquivoParaAnexar,
  AnexoDaConversa,
} from './central.js';
import type { ArtigoAjuda } from './index.js';
// O idioma corrente e lido A CADA CHAMADA de conhecimento (ADR 0060), e nao na criacao da
// fonte: o chassi memoriza a fonte, e um idioma capturado ali congelaria a primeira escolha.
import { idiomaAtual, type Idioma } from '@complexity/i18n';

/**
 * De onde sai o token e o tenant da chamada.
 *
 * FUNCAO E NAO VALOR, e isso importa: o token EXPIRA e e renovado, e um valor capturado na
 * montagem envelheceria dentro do componente sem ninguem perceber. E o mesmo contrato de
 * `CredenciaisAjuda` e de `CredenciaisChat`, repetido aqui de proposito: importa-lo de um dos
 * dois faria esta peca depender de qual dos dois transportes existe.
 */
export interface CredenciaisCentral {
  readonly obter: () => { readonly token?: string; readonly tenantId?: string };
}

/** O que a Central precisa saber para falar com os dois servicos. */
export interface ConfiguracaoDaCentral {
  /** A base do `services/knowledge`. Atende categorias, busca, artigos e corpo. */
  readonly conhecimento: string | undefined;
  /** A base do `services/messaging`. Atende as tres de conversa. */
  readonly mensagens: string | undefined;
  /**
   * O produto de quem esta olhando, e ele NAO e o namespace.
   *
   * SAO DOIS EIXOS DIFERENTES, e confundi-los produziria dois defeitos opostos. O
   * `namespace` recorta a BASE DE CONHECIMENTO e chega em toda chamada porque o contrato
   * `FonteCentral` o exige. O `produto_id` recorta as CONVERSAS, e a 0010 escreve o porque na
   * propria DDL: *"uma conversa sobre um produto nao aparece no sino de outro, mesmo
   * sendo do mesmo cliente"*.
   *
   * HOJE OS DOIS COINCIDEM em cada produto (o `id` do manifesto e o `ajuda.namespace:
   * declarado em `packages/catalogo`), e e justamente por isso que ele e parametro
   * separado: derivar um do outro funcionaria ate o primeiro produto que os separe, e ali
   * quebraria sem erro nenhum aparecer, mostrando conversa de outro produto na Central.
   */
  readonly produtoId: string;
  readonly credenciais?: CredenciaisCentral;
}

/**
 * A forma CRUA que o `services/messaging` devolve.
 *
 * DECLARADA AQUI e nao importada do servico: camada 1 nao importa camada 2, e o que liga os
 * dois lados e o JSON que atravessa o HTTP. Datas chegam como TEXTO, e nao como `Date`.
 */
interface ConversaCrua {
  readonly id: string;
  readonly assunto: string;
  readonly criadaEm: string;
  readonly tipo: string;
  readonly categoria: string | null;
  readonly estado: string;
  readonly mensagens: readonly MensagemCrua[];
}

interface MensagemCrua {
  readonly id: string;
  readonly autor: string;
  readonly texto: string;
  readonly enviadaEm: string;
}

/**
 * O ESTADO do servico traduzido para a SITUACAO que a pessoa le.
 *
 * A TRADUCAO MORA AQUI, na camada da tela, e nao no servico, e a razao e que os dois
 * vocabularios servem a leitores diferentes. `public.estado_conversa` tem `aberta`, que e
 * verdade para quem TRIA: a conversa esta na fila. Para quem PERGUNTOU, "aberta" nao diz
 * nada, e `central.ts:69` ja tinha decidido o texto dela: *"da cadeira da pessoa, uma
 * conversa em que ela acabou de escrever esta em analise"*.
 *
 * MUDAR O SERVICO PARA FALAR `em_analise` QUEBRARIA O PAINEL DE QUEM OPERA, que consome
 * `estado` pelo `packages/chat` e mostra a fila com o vocabulario da triagem. Cada lado
 * mantem o seu, e a traducao acontece uma vez, aqui.
 */
const SITUACAO_DO_ESTADO: Readonly<Record<string, SituacaoDaConversa>> = Object.freeze({
  aberta: 'em_analise',
  respondida: 'respondida',
  encerrada: 'encerrada',
});

/**
 * Os cinco tipos, para validar o que veio do servico contra uma lista fechada.
 *
 * POR QUE VALIDAR ALGO QUE O ENUM DO BANCO JA GARANTE: os valores coincidem HOJE, e a
 * coincidencia e deliberada (`central.ts:50` escolheu o vocabulario da casa, e
 * `public.tipo_conversa` da 0023 tem os mesmos cinco). Um sexto tipo somado no banco chegaria
 * aqui como texto que o TypeScript acredita ser `TipoDeConversa` sem ser, e o defeito
 * apareceria como rotulo em branco na tela, longe da causa.
 */
const TIPOS: readonly TipoDeConversa[] = ['duvida', 'suporte', 'reclamacao', 'elogio', 'sugestao'];

export function criarFonteCentralHttp(configuracao: ConfiguracaoDaCentral): FonteCentral {
  const { produtoId, credenciais } = configuracao;
  const raizConhecimento = (configuracao.conhecimento ?? '').replace(/\/+$/, '');
  const raizMensagens = (configuracao.mensagens ?? '').replace(/\/+$/, '');

  /** Os cabecalhos, montados a cada chamada para o token nunca estar velho. */
  function cabecalhos(extra?: Record<string, string>): Record<string, string> {
    const c: Record<string, string> = { Accept: 'application/json', ...extra };
    const atual = credenciais?.obter();
    if (atual?.token) c.Authorization = `Bearer ${atual.token}`;
    // Omitido quando ausente, e nao mandado vazio: string vazia chega ao servidor como tenant
    // presente e falso, que e pior que tenant ausente.
    if (atual?.tenantId) c['X-Tenant-Id'] = atual.tenantId;
    return c;
  }

  /**
   * Uma chamada, com a raiz escolhida por quem chama.
   *
   * A RAIZ E PARAMETRO e nao constante porque sao DUAS bases: um so `pedir` com a raiz fixa
   * obrigaria a duplicar esta funcao inteira, e as duas copias divergiriam no primeiro ajuste
   * de cabecalho, que e exatamente o tipo de defeito que aparece meses depois em uma das duas.
   */
  async function pedir<T>(raiz: string, qual: string, caminho: string, init?: RequestInit): Promise<T> {
    if (raiz === '') {
      // A MENSAGEM DIZ QUAL DAS DUAS FALTA. "A API nao esta configurada" mandaria quem for
      // resolver conferir as duas variaveis de ambiente, e a Central e a unica tela que
      // depende das duas ao mesmo tempo.
      throw new Error(`A base de ${qual} não está configurada neste ambiente.`);
    }
    const resposta = await fetch(`${raiz}${caminho}`, {
      // SEM `credentials`, e isso NAO e descuido: a sessao viaja no cabecalho
      // `Authorization`, e nao em cookie. Com 'include', o navegador exige
      // `Access-Control-Allow-Credentials` no preflight, o servico nao o manda (decisao
      // contra CSRF), e a chamada e RECUSADA antes de sair. O `curl` passava porque nao faz
      // preflight, e por isso o defeito parecia de rede.
      headers: cabecalhos(init?.body === undefined ? undefined : { 'Content-Type': 'application/json' }),
      ...init,
    });
    if (!resposta.ok) {
      // O NUMERO VAI PARA O LOG, e nunca para a tela. Quem resolve precisa dele para separar
      // "servico fora" de "sem permissao"; quem opera nao tem o que fazer com um 401.
      console.warn(`[ajuda] ${qual}: ${caminho} respondeu ${resposta.status}.`);

      // A FRASE DA TELA SAI DO CORPO DA RESPOSTA, que o servico ja manda em portugues e ja
      // distingue os casos: "Token de acesso ausente." e "Sessao invalida ou expirada." sao
      // coisas diferentes, e o numero 401 e o mesmo nas duas. Ler o corpo entrega MAIS causa
      // que o codigo, e nao menos.
      const dito = await resposta
        .clone()
        .json()
        .then((c: unknown) =>
          typeof c === 'object' && c !== null && typeof (c as { message?: unknown }).message === 'string'
            ? ((c as { message: string }).message)
            : '',
        )
        .catch(() => '');

      // SEM CORPO UTIL, a frase e generica e diz O QUE FAZER, e nao o que houve: e o unico
      // caso em que a causa real de fato nao chegou ate aqui, e fingir precisao seria pior.
      throw new Error(dito !== '' ? dito : 'Não foi possível falar com o serviço agora. Tente de novo em instantes.');
    }
    return (await resposta.json()) as T;
  }

  /** Ha token para chamar? Ver o porque em cada uso. */
  function temToken(): boolean {
    return Boolean(credenciais?.obter().token);
  }

  /**
   * A conversa crua virando a conversa do contrato.
   *
   * A CONVERSAO ACONTECE NA FRONTEIRA, uma vez, e nao em cada componente: se cada tela
   * traduzisse o estado, uma delas esqueceria, e a mesma conversa apareceria "aberta" numa e
   * "em análise" noutra, que e a versao pequena do defeito que este arquivo fecha.
   */
  function comoConversa(crua: ConversaCrua): ConversaAjuda {
    return {
      id: crua.id,
      assunto: crua.assunto,
      // Tipo desconhecido cai em `duvida`, que e o padrao do proprio servico ao abrir: o
      // rotulo fica certo em vez de ficar em branco, e a tela nao precisa saber que houve
      // divergencia entre o enum do banco e esta lista.
      tipo: TIPOS.includes(crua.tipo as TipoDeConversa) ? (crua.tipo as TipoDeConversa) : 'duvida',
      categoria: crua.categoria,
      // Estado desconhecido cai em `em_analise` pela mesma razao, e porque e o mais seguro dos
      // tres: dizer que algo esta em analise quando ja foi encerrado gera uma pergunta a mais;
      // dizer que foi encerrado quando ainda esta na fila faz a pessoa parar de esperar.
      situacao: SITUACAO_DO_ESTADO[crua.estado] ?? 'em_analise',
      em: crua.criadaEm,
      turnos: crua.mensagens.map(comoTurno),
    };
  }

  /**
   * A mensagem crua virando um turno.
   *
   * `sistema` VIRA `equipe`, e nao um terceiro autor: `TurnoDaConversa` tem dois de proposito
   * (`central.ts:85`), e do ponto de vista de quem perguntou uma mensagem automatica veio do
   * outro lado do balcao igual. Inventar um terceiro rotulo obrigaria a pessoa a decidir o
   * que ele significa.
   */
  function comoTurno(crua: MensagemCrua): TurnoDaConversa {
    return {
      id: crua.id,
      autor: crua.autor === 'cliente' ? 'pessoa' : 'equipe',
      texto: crua.texto,
      em: crua.enviadaEm,
    };
  }

  /**
   * A lista de conversas, com o fio inteiro, numa chamada.
   *
   * `fio=1` E O QUE FAZ ISTO SER UMA CHAMADA E NAO N+1, e o contrato exige: `ConversaAjuda`
   * traz os turnos dentro, porque *"a conversa e um fio unico"*.
   *
   * `historico=1` PORQUE A ENCERRADA NAO SOME desta tela. `SituacaoDaConversa` tem
   * `encerrada` como um dos tres estados, entao esconde-la faria a tela nunca poder mostrar um
   * terco do proprio contrato, e quem perguntou perderia o registro no instante em que a
   * equipe encerrou.
   *
   * `como=cliente` PORQUE ESTA E A TELA DE QUEM PERGUNTA. E o lado que decide o que conta como
   * nao lida, e a Central de Ajuda nunca e a tela da operacao: aquela e o painel do admin.
   */
  /**
   * Diz ao servico que a pessoa OLHOU o sino agora.
   *
   * NUNCA LANCA, e a razao esta em quem chama: ela roda ao lado da lista de conversas, e a
   * lista e o assunto da tela. Uma falha de rede no carimbo nao pode virar tela vazia nem
   * aviso de indisponibilidade, porque o que a pessoa veio ver chegou bem.
   *
   * O CUSTO DE FALHAR E CONHECIDO E PEQUENO: o sino continua com o numero de antes, e a
   * proxima chegada na Ajuda tenta de novo. O oposto seria bem pior: engolir a lista para
   * proteger o contador.
   *
   * SEM CORPO, de proposito. Quem carimba e sempre quem esta na sessao, e o servico nao aceita
   * id de fora justamente para ninguem zerar o sino de outra pessoa
   * (services/messaging/src/sino/sino.controller.ts).
   */
  async function marcarSinoVisto(): Promise<void> {
    try {
      await pedir<{ registrado: boolean }>(raizMensagens, 'mensagens', '/sino/visto', {
        method: 'POST',
      });
    } catch {
      // Silencio DECLARADO, e nao esquecido: `pedir` ja registrou o status no console para
      // quem for depurar, e repetir o aviso aqui so encheria o log de quem procura outra coisa.
      return;
    }
  }

  async function listar(): Promise<readonly ConversaAjuda[]> {
    const p = encodeURIComponent(produtoId);
    const cruas = await pedir<readonly ConversaCrua[]>(
      raizMensagens,
      'mensagens',
      `/conversas?como=cliente&historico=1&fio=1&produto=${p}`,
    );
    return cruas.map(comoConversa);
  }

  /**
   * Acha uma conversa pelo id, relendo a lista.
   *
   * POR QUE RELER: `POST /conversas` devolve so o `id`, e `POST /conversas/:id/mensagens`
   * devolve so a MENSAGEM gravada. Os dois metodos do contrato precisam devolver a
   * `ConversaAjuda` inteira, com `situacao` e `em` vindos do BANCO.
   *
   * A ALTERNATIVA SERIA MONTAR A CONVERSA AQUI, com o relogio do navegador e um estado
   * adivinhado, e ela e pior de um jeito especifico: a tela mostraria uma conversa que
   * CONFERE com o que a pessoa acabou de digitar e DIVERGE do que o banco guardou, e a
   * divergencia so apareceria na proxima carga da pagina. E a mesma familia de defeito que
   * este arquivo existe para fechar, em escala de segundos.
   *
   * O CUSTO E UMA CHAMADA A MAIS em duas acoes RARAS (abrir e responder), e nao em toda carga
   * de tela. Vale o preco.
   */
  async function relerConversa(id: string): Promise<ConversaAjuda> {
    const conversa = (await listar()).find((c) => c.id === id);
    if (!conversa) {
      // Chegar aqui significa que a escrita deu certo e a leitura seguinte nao a viu. Nao ha
      // resposta honesta a inventar: devolver uma conversa montada faria a tela mostrar um
      // dado que o banco nao confirmou.
      throw new Error('A conversa foi registrada, mas não voltou na lista.');
    }
    return conversa;
  }

  return {
    async categorias(namespace: string, idioma?: Idioma): Promise<readonly CategoriaAjuda[]> {
      // SEM TOKEN, NAO SE CHAMA: a policy da 0010 devolveria so o conteudo da plataforma, e a
      // chamada prematura toma 401 e deixa "a ajuda nao respondeu" na tela, erro que a chamada
      // boa, feita um segundo depois, nao apaga.
      if (!temToken()) return [];
      const ns = encodeURIComponent(namespace);
      // O IDIOMA VAI NA QUERY, e nao no caminho (ADR 0060): os padroes pre-tenant do ADR 0044
      // tem contagem de segmentos fixa, e o interceptor compara `path`, que nao inclui a query.
      // AUSENTE, vale o idioma da tela NESTE instante, e nao o da criacao da fonte, que o
      // chassi memoriza e que congelaria a primeira escolha da pessoa.
      const i = encodeURIComponent(idioma ?? idiomaAtual());
      return pedir<readonly CategoriaAjuda[]>(raizConhecimento, 'conhecimento', `/categorias/${ns}?idioma=${i}`);
    },

    async buscar(namespace: string, termo: string, idioma?: Idioma): Promise<readonly ArtigoAjuda[]> {
      if (!temToken()) return [];
      // `encodeURIComponent` nos dois: o termo vem do que a pessoa digitou, e um `&` ou `#`
      // ali cortaria a busca ao meio sem erro nenhum aparecer.
      const ns = encodeURIComponent(namespace);
      const t = encodeURIComponent(termo);
      // Mesmo criterio de `categorias` para o idioma.
      const i = encodeURIComponent(idioma ?? idiomaAtual());
      return pedir<readonly ArtigoAjuda[]>(raizConhecimento, 'conhecimento', `/artigos/${ns}?termo=${t}&idioma=${i}`);
    },

    async artigosDaCategoria(
      namespace: string,
      categoriaId: string,
      idioma?: Idioma,
    ): Promise<readonly ArtigoAjuda[]> {
      if (!temToken()) return [];
      const ns = encodeURIComponent(namespace);
      const cat = encodeURIComponent(categoriaId);
      // Mesmo criterio de `categorias` para o idioma.
      const i = encodeURIComponent(idioma ?? idiomaAtual());
      return pedir<readonly ArtigoAjuda[]>(raizConhecimento, 'conhecimento', `/categorias/${ns}/${cat}?idioma=${i}`);
    },

    async corpo(namespace: string, artigoId: string, idioma?: Idioma): Promise<string | null> {
      if (!temToken()) return null;
      const ns = encodeURIComponent(namespace);
      const artigo = encodeURIComponent(artigoId);
      // Mesmo criterio de `categorias` para o idioma: o corpo precisa vir no MESMO idioma do
      // titulo que a lista mostrou, senao a pessoa abre um artigo ingles e le portugues.
      const i = encodeURIComponent(idioma ?? idiomaAtual());
      try {
        // `corpo_md` e o nome que a rota devolve, medido em
        // services/knowledge/src/artigos/artigos.controller.ts. Escrever `corpo` aqui
        // produziria `undefined` silencioso: a chamada daria certo, a tela abriria, e o artigo
        // apareceria em branco sem erro nenhum.
        const r = await pedir<{ readonly corpo_md: string }>(
          raizConhecimento,
          'conhecimento',
          `/artigos/${ns}/${artigo}?idioma=${i}`,
        );
        return r.corpo_md;
      } catch {
        // O contrato manda devolver `null` quando nao acha, e a rota devolve 404 tanto para
        // artigo inexistente quanto para artigo de outro cliente. A indistincao e proposital no
        // servico, e mante-la aqui e o que impede a tela de contar a diferenca.
        return null;
      }
    },

    /**
     * O `namespace` NAO ENTRA AQUI, e a ausencia e decisao e nao esquecimento: conversa se
     * recorta por PRODUTO, e nao por namespace de ajuda. Ver `ConfiguracaoDaCentral.produtoId`.
     */
    async minhasConversas(_namespace: string): Promise<readonly ConversaAjuda[]> {
      // SEM TOKEN, LISTA VAZIA e nao erro: a policy devolveria zero conversas de qualquer
      // jeito, e "voce ainda nao escreveu nada" e a leitura correta de uma sessao sem tenant.
      if (!temToken()) return [];
      const lista = await listar();

      /*
        AQUI O SINO ZERA, e este e o unico lugar da plataforma que carimba o visto.

        PEDIDO DELE EM 09/09/2026, com a captura da barra superior: "O SINO, QUANDO CLICADO
        DEVERA APAGAR O NUMERO DELE, POIS JA ESTA LEVANDO PARA O MENU DE AJUDA ONDE FICAM AS
        MENSAGENS." O contador que nunca zera ensina a ignorar o sino, e ai ele deixa de
        avisar quando importa.

        POR QUE AQUI E NAO NO CLIQUE DO SINO: o clique chama `aoNavegar('/ajuda')`, e o item
        Ajuda do menu chama exatamente a mesma coisa (Chassi.tsx:184 e :220). Do lado do app
        os dois sao indistinguiveis, e ele decidiu por enquete que "chegar na Ajuda" serve:
        quem chegou por qualquer caminho viu as mensagens. Este metodo roda UMA vez por
        chegada, no efeito de montagem da Central (CentralDeAjuda.tsx:296).

        DEPOIS DA LISTA E NAO ANTES, e a ordem foi escolhida. Entre carimbar e listar cabe uma
        mensagem nova: carimbando antes, ela apareceria na lista e NAO contaria no sino, e o
        sino estaria mentindo por baixo. Carimbando depois, ela conta, e o pior caso e o sino
        mostrar 1 para algo que ja esta na tela. A duvida cai para o lado que MOSTRA, que e a
        mesma escolha do `-infinity` na 0070.

        NAO ESPERA E NAO DERRUBA A TELA. O carimbo e conforto, e a lista e o assunto: uma
        falha aqui nao pode esconder as conversas. Sem `await`, a lista aparece no mesmo
        instante em que apareceria antes desta linha existir.

        ISTO NAO MARCA NADA COMO LIDO, e essa e a decisao inteira do ADR 0059: `lida_em`
        continua sendo carimbado por quem DESENHA o texto, entao a etiqueta "Novo" e o
        contador de cada conversa sobrevivem na tela para onde o sino acabou de trazer a
        pessoa. Marcar leitura aqui zeraria o sino e apagaria junto a resposta a pergunta
        seguinte, que e "quais eram os novos?".
      */
      void marcarSinoVisto();

      return lista;
    },

    async abrirConversa(_namespace: string, nova: NovaConversa): Promise<ConversaAjuda> {
      /**
       * A CATEGORIA SUGERIDA VAI DENTRO DA MENSAGEM, e isto e uma DIVERGENCIA declarada.
       *
       * O QUE FOI MEDIDO em 05/09/2026: `NovaConversa.categoriaSugerida` cumpre o passo 21 do
       * manual, e NAO HA COLUNA para ele. A 0023 criou `conversas.categoria_id`, que e uma
       * referencia a uma categoria que EXISTE; o nome de uma que FALTA nao cabe ali.
       *
       * AS TRES SAIDAS, e por que esta:
       *   1. Descartar em silencio. Recusada: a pessoa escreveu o nome de uma categoria que
       *      falta, e quem cura a base nunca ficaria sabendo. E o passo 21 virando nada.
       *   2. Devolver na conversa criada sem gravar. Recusada, e e a PIOR: a etiqueta
       *      apareceria ao abrir e sumiria na proxima carga da pagina. Duas telas contando
       *      historias diferentes sobre a mesma conversa, que e o defeito que esta peca fecha.
       *   3. Gravar dentro do texto, marcada. Escolhida: sobrevive a recarga, e a operacao le
       *      no fio, que e onde ela ja esta lendo.
       *
       * O LIMITE E HONESTO E FICA ESCRITO: assim ela NAO e dado estruturado. Ninguem consegue
       * perguntar ao banco "quais categorias os usuarios pediram", que e a pergunta que o
       * passo 21 quer responder. Fechar isso exige coluna nova, e migration e decisao do
       * fundador, e nao desta peca.
       */
      const mensagem =
        nova.categoriaSugerida === null || nova.categoriaSugerida.trim() === ''
          ? nova.mensagem
          : `${nova.mensagem}\n\nCategoria sugerida por quem escreveu: ${nova.categoriaSugerida.trim()}`;

      const criada = await pedir<{ readonly id: string }>(raizMensagens, 'mensagens', '/conversas', {
        method: 'POST',
        body: JSON.stringify({
          produtoId,
          assunto: nova.assunto,
          tipo: nova.tipo,
          // `texto` e o nome que a rota espera, medido em
          // services/messaging/src/conversas/conversas.controller.ts. O contrato daqui chama o
          // mesmo campo de `mensagem`, e a traducao acontece nesta linha e em nenhuma outra.
          texto: mensagem,
          categoriaId: nova.categoriaId,
          como: 'cliente',
        }),
      });

      return relerConversa(criada.id);
    },

    async responder(_namespace: string, conversaId: string, texto: string): Promise<ConversaAjuda> {
      const id = encodeURIComponent(conversaId);
      await pedir<unknown>(raizMensagens, 'mensagens', `/conversas/${id}/mensagens`, {
        method: 'POST',
        body: JSON.stringify({ texto, como: 'cliente' }),
      });

      // A CONVERSA E RELIDA e nao remendada com o turno novo: o servico muda o ESTADO dela ao
      // receber a mensagem (cliente escrevendo volta a conversa para `aberta`), e a tela
      // precisa da situacao que o banco decidiu, e nao da que este arquivo adivinharia.
      return relerConversa(conversaId);
    },

    /**
     * ANEXA as imagens a uma mensagem, uma chamada por arquivo.
     *
     * UMA POR VEZ E NAO TODAS DE UMA VEZ, e a razao e o tamanho: cada imagem viaja em
     * base64, que infla 4/3, e juntar tres num corpo so multiplicaria por tres um pedido
     * que ja e grande. A rota do messaging tambem aceita um por chamada
     * (conversas.controller.ts:298), entao mandar em lote exigiria uma rota nova.
     *
     * EM SERIE E NAO EM PARALELO: tres envios simultaneos de dezenas de MB competem pela
     * banda de quem esta numa rede ruim, e o resultado e todos lentos em vez de um rapido
     * atras do outro. Quem anexa costuma ter uma ou duas imagens.
     *
     * O ERRO DE UM ARQUIVO INTERROMPE O RESTO, e isso e deliberado: a mensagem JA FOI
     * enviada quando esta funcao roda, entao um anexo que falha nao perde o texto da
     * pessoa. Quem chama decide o que dizer, e o texto dela esta salvo de qualquer forma.
     */
    async anexar(
      _namespace: string,
      conversaId: string,
      mensagemId: string,
      arquivos: readonly ArquivoParaAnexar[],
    ): Promise<void> {
      const idConversa = encodeURIComponent(conversaId);
      const idMensagem = encodeURIComponent(mensagemId);
      for (const arquivo of arquivos) {
        await pedir<unknown>(
          raizMensagens,
          'mensagens',
          `/conversas/${idConversa}/mensagens/${idMensagem}/anexos`,
          {
            method: 'POST',
            body: JSON.stringify({
              nomeArquivo: arquivo.nomeArquivo,
              tipoConteudo: arquivo.tipoConteudo,
              conteudoBase64: arquivo.base64,
            }),
          },
        );
      }
    },

    /** Os anexos da conversa inteira, numa chamada so. */
    async anexosDaConversa(_namespace: string, conversaId: string) {
      const id = encodeURIComponent(conversaId);
      return pedir<readonly AnexoDaConversa[]>(raizMensagens, 'mensagens', `/conversas/${id}/anexos`);
    },

    /**
     * UM anexo com o conteudo, para a tela abrir.
     *
     * DEVOLVE BASE64 e nao o binario: a chamada precisa dos cabecalhos de sessao, e uma tag
     * de imagem apontando para a URL nao carrega cabecalho nenhum. Com base64 a tela monta
     * o blob.
     */
    async anexo(_namespace: string, anexoId: string) {
      const id = encodeURIComponent(anexoId);
      const cru = await pedir<{
        nomeArquivo: string;
        tipoConteudo: string;
        conteudoBase64: string;
      }>(raizMensagens, 'mensagens', `/conversas/anexos/${id}`);
      // O NOME DO CAMPO MUDA AQUI de proposito: o servico devolve `conteudoBase64`, e o
      // contrato desta camada usa `base64`. Traduzir na fronteira e o que impede o nome do
      // servico de vazar para dentro dos componentes.
      return {
        nomeArquivo: cru.nomeArquivo,
        tipoConteudo: cru.tipoConteudo,
        base64: cru.conteudoBase64,
      };
    },
  };
}
